"use strict";
import { SwapEventName } from "@uniswap/analytics-events";
import { Protocol } from "@uniswap/router-sdk";
import { Pair } from "@uniswap/v2-sdk";
import { Pool as V3Pool } from "@uniswap/v3-sdk";
import { Pool as V4Pool } from "@uniswap/v4-sdk";
import { useEffect } from "react";
import { useAccountMeta } from "contexts/UniswapContext";
import { Routing } from "data/tradingApi/__generated__";
import { getChainLabel } from "features/chains/utils";
import { usePortfolioTotalValue } from "features/dataApi/balances";
import { useLocalizationContext } from "features/language/LocalizationContext";
import { sendAnalyticsEvent } from "features/telemetry/send";
import { ValueType, getCurrencyAmount } from "features/tokens/getCurrencyAmount";
import { getTokenProtectionWarning } from "features/tokens/safetyUtils";
import { SwapEventType, timestampTracker } from "features/transactions/swap/utils/SwapEventTimestampTracker";
import { slippageToleranceToPercent } from "features/transactions/swap/utils/format";
import { getSwapFeeUsd } from "features/transactions/swap/utils/getSwapFeeUsd";
import { isClassic, isUniswapX } from "features/transactions/swap/utils/routing";
import { getClassicQuoteFromResponse } from "features/transactions/swap/utils/tradingApi";
import { TransactionOriginType } from "features/transactions/types/transactionDetails";
import { CurrencyField } from "types/currency";
import { getCurrencyAddressForAnalytics } from "utils/uniswapCurrencyId";
import { NumberType } from "utilities/src/format/types";
import { logger } from "utilities/src/logger/logger";
import { useTrace } from "utilities/src/telemetry/trace/TraceContext";
const DEFAULT_RESULT = {
  v2Used: false,
  v3Used: false,
  v4Used: false,
  uniswapXUsed: false
};
function getPoolAddress(pool) {
  if (pool instanceof Pair) {
    return Pair.getAddress(pool.token0, pool.token1);
  } else if (pool instanceof V3Pool) {
    return V3Pool.getAddress(pool.token0, pool.token1, pool.fee);
  } else if (pool instanceof V4Pool) {
    return pool.poolId;
  }
  return void 0;
}
function getClassicPoolProtocol(pool) {
  if (pool instanceof Pair) {
    return "V2";
  } else if (pool instanceof V3Pool) {
    return "V3";
  } else if (pool instanceof V4Pool) {
    return "V4";
  }
  return void 0;
}
function getRoutings(routes) {
  return routes.map((route) => route.pools);
}
export function getRouteAnalyticsData({
  routing,
  routes
}) {
  if (!routing) {
    return void 0;
  }
  if (isClassic({ routing }) && routes) {
    const routings = getRoutings(routes);
    const paths = routings.map(
      (route) => route.map((pool) => ({
        poolAddress: getPoolAddress(pool) ?? "unknown",
        version: getClassicPoolProtocol(pool) ?? "unknown"
      }))
    );
    const v2Used = paths.some((path) => path.some((pool) => pool.version === Protocol.V2));
    const v3Used = paths.some((path) => path.some((pool) => pool.version === Protocol.V3));
    const v4Used = paths.some((path) => path.some((pool) => pool.version === Protocol.V4));
    const poolsCount = paths.reduce((acc, path) => acc + path.length, 0);
    return {
      poolsCount,
      paths,
      v2Used,
      v3Used,
      v4Used,
      // For classic trades, X is not used
      uniswapXUsed: false
    };
  }
  if (isUniswapX({ routing })) {
    return {
      ...DEFAULT_RESULT,
      uniswapXUsed: true
    };
  }
  return DEFAULT_RESULT;
}
export function useSwapAnalytics(derivedSwapInfo) {
  const formatter = useLocalizationContext();
  const trace = useTrace();
  const {
    trade: { trade }
  } = derivedSwapInfo;
  const quoteId = trade?.quote?.requestId;
  const account = useAccountMeta();
  const { data: portfolioData } = usePortfolioTotalValue({
    address: account?.address,
    fetchPolicy: "cache-first"
  });
  useEffect(() => {
    if (!trade) {
      return;
    }
    sendAnalyticsEvent(
      SwapEventName.SWAP_QUOTE_RECEIVED,
      getBaseTradeAnalyticsProperties({
        formatter,
        trade,
        currencyInAmountUSD: derivedSwapInfo.currencyAmountsUSDValue.input,
        currencyOutAmountUSD: derivedSwapInfo.currencyAmountsUSDValue.output,
        portfolioBalanceUsd: portfolioData?.balanceUSD,
        trace
      })
    );
  }, [quoteId]);
  return;
}
export function getBaseTradeAnalyticsProperties({
  formatter,
  trade,
  currencyInAmountUSD,
  currencyOutAmountUSD,
  portfolioBalanceUsd,
  presetPercentage,
  preselectAsset,
  trace,
  isBatched,
  includedPermitTransactionStep
}) {
  const portionAmount = getClassicQuoteFromResponse(trade?.quote)?.portionAmount;
  const feeCurrencyAmount = getCurrencyAmount({
    value: portionAmount,
    valueType: ValueType.Raw,
    currency: trade.outputAmount.currency
  });
  const classicQuote = getClassicQuoteFromResponse(trade?.quote);
  const finalOutputAmount = feeCurrencyAmount ? trade.outputAmount.subtract(feeCurrencyAmount) : trade.outputAmount;
  const slippagePercent = slippageToleranceToPercent(trade.slippageTolerance ?? 0);
  return {
    ...trace,
    routing: tradeRoutingToFillType(trade),
    total_balances_usd: portfolioBalanceUsd,
    token_in_symbol: trade.inputAmount.currency.symbol,
    token_out_symbol: trade.outputAmount.currency.symbol,
    token_in_address: getCurrencyAddressForAnalytics(trade.inputAmount.currency),
    token_out_address: getCurrencyAddressForAnalytics(trade.outputAmount.currency),
    price_impact_basis_points: isClassic(trade) ? trade.priceImpact?.multiply(100).toSignificant() : void 0,
    chain_id: trade.inputAmount.currency.chainId === trade.outputAmount.currency.chainId ? trade.inputAmount.currency.chainId : void 0,
    chain_id_in: trade.inputAmount.currency.chainId,
    chain_id_out: trade.outputAmount.currency.chainId,
    token_in_amount: trade.inputAmount.toExact(),
    token_out_amount: formatter.formatCurrencyAmount({
      value: finalOutputAmount,
      type: NumberType.SwapTradeAmount
    }),
    token_in_amount_usd: currencyInAmountUSD ? parseFloat(currencyInAmountUSD.toFixed(2)) : void 0,
    token_out_amount_usd: currencyOutAmountUSD ? parseFloat(currencyOutAmountUSD.toFixed(2)) : void 0,
    preset_percentage: presetPercentage,
    preselect_asset: preselectAsset,
    allowed_slippage: trade.slippageTolerance !== void 0 ? parseFloat(trade.slippageTolerance.toFixed(2)) : void 0,
    allowed_slippage_basis_points: trade.slippageTolerance ? trade.slippageTolerance * 100 : void 0,
    fee_amount: portionAmount,
    requestId: trade.quote?.requestId,
    ura_request_id: trade.quote?.requestId,
    ura_block_number: isClassic(trade) ? trade.quote?.quote.blockNumber : void 0,
    quoteId: classicQuote?.quoteId,
    transactionOriginType: TransactionOriginType.Internal,
    swap_quote_block_number: classicQuote?.blockNumber,
    estimated_network_fee_usd: isClassic(trade) ? trade.quote?.quote.gasFeeUSD : void 0,
    fee_usd: currencyOutAmountUSD ? getSwapFeeUsd({
      trade,
      outputAmount: trade.outputAmount,
      outputAmountUsd: currencyOutAmountUSD
    }) : void 0,
    type: trade.tradeType,
    minimum_output_after_slippage: trade.minimumAmountOut(slippagePercent).toSignificant(6),
    token_in_amount_max: trade.maximumAmountIn(slippagePercent).toExact(),
    token_out_amount_min: trade.minimumAmountOut(slippagePercent).toExact(),
    token_in_detected_tax: parseFloat(trade.inputTax.toFixed(2)),
    token_out_detected_tax: parseFloat(trade.outputTax.toFixed(2)),
    simulation_failure_reasons: isClassic(trade) ? trade.quote?.quote.txFailureReasons : void 0,
    ...getRouteAnalyticsData(trade),
    is_batch: isBatched,
    included_permit_transaction_step: includedPermitTransactionStep
  };
}
export function getBaseTradeAnalyticsPropertiesFromSwapInfo({
  transactionSettings,
  derivedSwapInfo,
  trace
}) {
  const { chainId, currencyAmounts, currencyAmountsUSDValue } = derivedSwapInfo;
  const inputCurrencyAmount = currencyAmounts[CurrencyField.INPUT];
  const outputCurrencyAmount = currencyAmounts[CurrencyField.OUTPUT];
  const currencyInAmountUSD = currencyAmountsUSDValue[CurrencyField.INPUT] ? parseFloat(currencyAmountsUSDValue[CurrencyField.INPUT].toFixed(2)) : void 0;
  const currencyOutAmountUSD = currencyAmountsUSDValue[CurrencyField.OUTPUT] ? parseFloat(currencyAmountsUSDValue[CurrencyField.OUTPUT].toFixed(2)) : void 0;
  const slippageTolerance = transactionSettings.customSlippageTolerance ?? transactionSettings.autoSlippageTolerance;
  const portionAmount = getClassicQuoteFromResponse(derivedSwapInfo.trade?.trade?.quote)?.portionAmount;
  const feeCurrencyAmount = getCurrencyAmount({
    value: portionAmount,
    valueType: ValueType.Raw,
    currency: outputCurrencyAmount?.currency
  });
  const finalOutputAmount = outputCurrencyAmount && feeCurrencyAmount ? outputCurrencyAmount.subtract(feeCurrencyAmount) : outputCurrencyAmount;
  const trade = derivedSwapInfo.trade.trade;
  return {
    ...trace,
    token_in_symbol: inputCurrencyAmount?.currency.symbol,
    token_out_symbol: outputCurrencyAmount?.currency.symbol,
    token_in_address: inputCurrencyAmount ? getCurrencyAddressForAnalytics(inputCurrencyAmount?.currency) : "",
    token_out_address: outputCurrencyAmount ? getCurrencyAddressForAnalytics(outputCurrencyAmount?.currency) : "",
    price_impact_basis_points: trade && isClassic(trade) ? trade?.priceImpact?.multiply(100)?.toSignificant() : void 0,
    estimated_network_fee_usd: void 0,
    chain_id: chainId,
    token_in_amount: inputCurrencyAmount?.toExact() ?? "",
    token_out_amount: finalOutputAmount?.toExact() ?? "",
    token_in_amount_usd: currencyInAmountUSD,
    token_out_amount_usd: currencyOutAmountUSD,
    allowed_slippage_basis_points: slippageTolerance ? slippageTolerance * 100 : void 0,
    fee_amount: portionAmount,
    transactionOriginType: TransactionOriginType.Internal,
    tokenWarnings: {
      input: getTokenProtectionWarning(derivedSwapInfo.currencies.input),
      output: getTokenProtectionWarning(derivedSwapInfo.currencies.output)
    }
  };
}
export function logSwapQuoteFetch({
  chainId,
  isUSDQuote = false,
  isQuickRoute = false
}) {
  let performanceMetrics = {};
  if (!isUSDQuote) {
    const hasSetSwapQuote = timestampTracker.hasTimestamp(SwapEventType.FirstQuoteFetchStarted);
    const elapsedTime = timestampTracker.setElapsedTime(SwapEventType.FirstQuoteFetchStarted);
    const time_to_first_quote_request = hasSetSwapQuote ? void 0 : elapsedTime;
    const time_to_first_quote_request_since_first_input = hasSetSwapQuote ? void 0 : timestampTracker.getElapsedTime(SwapEventType.FirstQuoteFetchStarted, SwapEventType.FirstSwapAction);
    performanceMetrics = { time_to_first_quote_request, time_to_first_quote_request_since_first_input };
  }
  sendAnalyticsEvent(SwapEventName.SWAP_QUOTE_FETCH, { chainId, isQuickRoute, ...performanceMetrics });
  logger.info("analytics", "logSwapQuoteFetch", SwapEventName.SWAP_QUOTE_FETCH, {
    chainId,
    // we explicitly log it here to show on Datadog dashboard
    chainLabel: getChainLabel(chainId),
    isQuickRoute,
    ...performanceMetrics
  });
}
export function tradeRoutingToFillType({
  routing,
  indicative
}) {
  if (indicative) {
    return "none";
  }
  switch (routing) {
    case Routing.DUTCH_V3:
      return "uniswap_x_v3";
    case Routing.DUTCH_V2:
      return "uniswap_x_v2";
    case Routing.DUTCH_LIMIT:
      return "uniswap_x";
    case Routing.PRIORITY:
      return "priority_order";
    case Routing.LIMIT_ORDER:
      return "limit_order";
    case Routing.CLASSIC:
      return "classic";
    case Routing.BRIDGE:
      return "bridge";
    default:
      return "none";
  }
}
